import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import type { FC, ReactNode } from 'react';
import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { MuiFormSelect } from 'components/Form';
import type { MuiFormSelectOptionT } from 'components/Form/MuiFormSelect/MuiFormSelect';
import Popup from 'components/Popup/Popup';
import PriorityIcon from 'components/PriorityIcon/PriorityIcon';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import type { NoopT } from 'declarations/types/common';

import { getPopupContentStyle } from './ChangeTicketPriorityPopup.styled';
import useChangePriorityPopup from './hooks/useChangeTicketPriorityPopup';

type ChangeTicketPriorityPopupPropsT = {
  ticketId: string;
  ticketPriorityId: number;
  isOpen: boolean;
  onClose: NoopT;
};

export type ChangeTicketPriorityFormValuesT = {
  ticketId: string;
  ticketPriorityId: number;
};

const ChangeTicketPriorityPopup: FC<ChangeTicketPriorityPopupPropsT> = ({
  ticketId,
  ticketPriorityId,
  isOpen,
  onClose,
}) => {
  const { isMobile } = useResponsive();
  const {
    isChangingPriority,
    ticketPrioritiesOptions,
    isLoadingTicketPrioritiesOptions,
    handleChangeTicketPriority,
    handleClosePopup,
  } = useChangePriorityPopup();

  const fromMethods = useForm<ChangeTicketPriorityFormValuesT>({
    defaultValues: {
      ticketId: ticketId,
      ticketPriorityId: ticketPriorityId,
    },
    mode: 'onChange',
  });

  const { control, handleSubmit, setValue } = fromMethods;

  useEffect(() => {
    if (isOpen) {
      setValue('ticketId', ticketId);
      setValue('ticketPriorityId', ticketPriorityId);
    }
  }, [isOpen, ticketId, ticketPriorityId, setValue]);

  const selectedPriorityId = useWatch<ChangeTicketPriorityFormValuesT, 'ticketPriorityId'>({
    control,
    name: 'ticketPriorityId',
  });

  const muiOptions: MuiFormSelectOptionT[] = ticketPrioritiesOptions.map(o => ({
    value: o.value,
    label: o.label,
  }));

  const renderPriorityOption = (option: MuiFormSelectOptionT): ReactNode => (
    <Box display="flex" alignItems="center" gap={1}>
      <PriorityIcon priority={option.label} />
      {option.label}
    </Box>
  );

  return (
    <Popup
      nested
      isOpen={isOpen}
      popupId={ScrollLockIdE.ChangeTicketPriorityPopup}
      contentStyle={getPopupContentStyle(isMobile)}
      onClose={handleClosePopup(onClose)}
      closeOnEscape={false}
    >
      <Typography variant="h6" align="center" sx={{ mb: 2 }}>
        Change priority
      </Typography>
      <FormProvider {...fromMethods}>
        <Box
          component="form"
          onSubmit={handleSubmit(data => handleChangeTicketPriority(data, onClose))}
        >
          <Box sx={{ mb: 2 }}>
            <MuiFormSelect
              name="ticketPriorityId"
              label="Choose Ticket priority"
              placeholder="Ticket priorities"
              options={muiOptions}
              isLoading={isLoadingTicketPrioritiesOptions}
              renderOption={renderPriorityOption}
            />
          </Box>
          <Box display="flex" justifyContent={isMobile ? 'center' : 'flex-end'} gap={1}>
            <Button variant="outlined" onClick={handleClosePopup(onClose)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isChangingPriority || selectedPriorityId === ticketPriorityId}
            >
              {isChangingPriority ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      </FormProvider>
    </Popup>
  );
};

export default ChangeTicketPriorityPopup;
